import { Copy, Link2, Plus, Trash2 } from "lucide-react";

const STATUS_STYLE = { active: "bg-emerald-50 text-emerald-700", used: "bg-blue-50 text-blue-700" };
const STATUS_LABEL = { active: "Active", used: "Used", expired: "Expired" };
const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

const InviteRow = ({ invite, i, disableGuideInvite, deleteGuideInvite }) => (
  <div data-testid={`guide-invite-${i}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-background/60 px-4 py-3 text-sm">
    <div>
      <span data-testid={`guide-invite-status-${i}`} className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLE[invite.status] || "bg-muted text-muted-foreground"}`}>
        {STATUS_LABEL[invite.status] || "Disabled"}
      </span>
      <p className="mt-1 text-xs text-muted-foreground">
        Created {fmt(invite.created_at)}{invite.expires_at ? ` · Expires ${fmt(invite.expires_at)}` : " · No expiry"}{invite.used_at ? ` · Used ${fmt(invite.used_at)}` : ""}
      </p>
    </div>
    {invite.status === "active" ? (
      <button type="button" data-testid={`guide-invite-disable-${i}`} onClick={() => disableGuideInvite(invite)}
        className="rounded-full border border-destructive/40 px-4 py-1.5 text-xs text-destructive hover:bg-destructive hover:text-white transition-colors">
        Disable
      </button>
    ) : (
      <button type="button" data-testid={`guide-invite-delete-${i}`} onClick={() => deleteGuideInvite(invite)}
        className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs text-muted-foreground hover:bg-destructive hover:text-white hover:border-destructive transition-colors">
        <Trash2 size={13} /> Delete
      </button>
    )}
  </div>
);

export const GuideInvitesPanel = ({ guideInvites, lastGuideLink, inviteBusy, inviteDays, setInviteDays, createGuideInvite, copyGuideLink, disableGuideInvite, deleteGuideInvite }) => (
  <div className="rounded-2xl border border-accent/25 bg-accent/5 p-5 mb-6" data-testid="guide-invites-panel">
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
      <div>
        <p className="font-medium flex items-center gap-2"><Link2 size={17} className="text-accent" /> One-time guide submission links</p>
        <p className="text-sm text-muted-foreground mt-1">Create a private link for one guide. It closes automatically after submission, or you can disable it anytime.</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground" htmlFor="guide-invite-days">Active for</label>
          <select id="guide-invite-days" data-testid="guide-invite-days" value={inviteDays} onChange={(e) => setInviteDays(Number(e.target.value))}
            className="mt-1 w-full min-w-[9rem] bg-background/70 border border-border rounded-xl px-4 py-2.5 text-sm outline-none focus:border-accent">
            {[1, 3, 7, 14, 30].map((days) => <option key={days} value={days} label={`${days} day${days === 1 ? "" : "s"}`} />)}
          </select>
        </div>
        <button type="button" data-testid="create-guide-invite-btn" onClick={createGuideInvite} disabled={inviteBusy}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-60">
          <Plus size={16} /> {inviteBusy ? "Creating…" : "Create guide link"}
        </button>
      </div>
    </div>
    {lastGuideLink && (
      <div className="mb-4 flex flex-col sm:flex-row gap-2">
        <input readOnly data-testid="latest-guide-invite-link" value={lastGuideLink} className="flex-1 bg-background/70 border border-border rounded-xl px-4 py-2.5 text-sm outline-none" />
        <button type="button" data-testid="copy-latest-guide-invite" onClick={() => copyGuideLink(lastGuideLink)}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-accent text-accent px-4 py-2.5 text-sm hover:bg-accent hover:text-white transition-colors">
          <Copy size={15} /> Copy link
        </button>
      </div>
    )}
    {guideInvites.length > 0 ? (
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="guide-invite-list">
        {guideInvites.map((invite, i) => <InviteRow key={invite.id} invite={invite} i={i} disableGuideInvite={disableGuideInvite} deleteGuideInvite={deleteGuideInvite} />)}
      </div>
    ) : (
      <p className="text-sm text-muted-foreground" data-testid="guide-invite-empty">No guide links created yet.</p>
    )}
  </div>
);
